import { useEffect, useState } from 'react'
import {
  cancelExecution,
  executeTaskDraft,
  patchExecutionBody,
  sendExecution,
} from '../api/tasks'

type Props = {
  taskId: string
  open: boolean
  onClose: () => void
  onSent: () => void | Promise<void>
}

export function EllisExecuteModal({ taskId, open, onClose, onSent }: Props) {
  const [phase, setPhase] = useState<
    'generating' | 'review' | 'editing' | 'sending' | 'failed'
  >('generating')
  const [status, setStatus] = useState('Ellis is on it…')
  const [body, setBody] = useState('')
  const [edit, setEdit] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [executionId, setExecutionId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [runKey, setRunKey] = useState(0)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const clientRequestId = crypto.randomUUID()
    setPhase('generating')
    setStatus('Ellis is on it…')
    setBody('')
    setEdit('')
    setError(null)
    setExecutionId(null)
    setBusy(true)

    void executeTaskDraft(taskId, clientRequestId, (ev) => {
      if (cancelled) return
      if (ev.execution_id) setExecutionId(ev.execution_id)
      if (ev.phase === 'delta') {
        if (typeof ev.body === 'string') setBody(ev.body)
        else if (ev.delta) setBody((b) => b + ev.delta!)
        setStatus('Writing the reply…')
      } else if (ev.message) {
        setStatus(ev.message)
      }
    })
      .then((result) => {
        if (cancelled) return
        setExecutionId(result.execution_id)
        setBody(result.body)
        setEdit(result.body)
        setPhase('review')
        setStatus('')
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'execute_failed')
        setPhase('failed')
      })
      .finally(() => {
        if (!cancelled) setBusy(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, taskId, runKey])

  if (!open) return null

  const dismiss = (doCancel = true) => {
    const id = executionId
    onClose()
    if (doCancel && id && phase !== 'sending') {
      void cancelExecution(id).catch(() => undefined)
    }
  }

  const saveEdit = () => {
    if (!executionId || !edit.trim()) return
    setBusy(true)
    setError(null)
    void patchExecutionBody(executionId, edit.trim())
      .then((r) => {
        setBody(r.body)
        setEdit(r.body)
        setPhase('review')
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'save_failed'))
      .finally(() => setBusy(false))
  }

  const approveSend = () => {
    if (!executionId) return
    const text = (phase === 'editing' ? edit : body).trim()
    setBusy(true)
    setPhase('sending')
    setError(null)
    setStatus('Sending reply-all…')
    void sendExecution(executionId, text)
      .then(async () => {
        await onSent()
        onClose()
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'send_failed')
        setPhase('review')
      })
      .finally(() => {
        setBusy(false)
        setStatus('')
      })
  }

  return (
    <div className="ellis-modal-backdrop" role="presentation">
      <div
        className="ellis-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ellis-modal-title"
      >
        <header className="ellis-modal-head">
          <h2 id="ellis-modal-title">Ellis draft</h2>
          <p className="ellis-modal-sub">Reply-all on this thread · review before send</p>
        </header>

        {phase === 'generating' || phase === 'sending' ? (
          <p className="ellis-wait" role="status">
            {status || 'Working…'}
          </p>
        ) : null}

        {phase === 'editing' ? (
          <textarea
            className="ellis-draft-edit"
            value={edit}
            onChange={(e) => setEdit(e.target.value)}
            rows={14}
            disabled={busy}
          />
        ) : (
          <div className="ellis-draft" aria-live="polite">
            {body || (phase === 'generating' ? '…' : '(empty)')}
          </div>
        )}

        {error ? (
          <p className="ellis-error" role="alert">
            {error}
          </p>
        ) : null}

        <footer className="ellis-modal-actions">
          {phase === 'failed' ? (
            <>
              <button
                type="button"
                className="ellis"
                onClick={() => {
                  setError(null)
                  setRunKey((k) => k + 1)
                }}
              >
                Retry
              </button>
              <button type="button" className="quiet" onClick={() => dismiss(false)}>
                Close
              </button>
            </>
          ) : null}
          {phase === 'review' ? (
            <>
              <button type="button" className="ellis" disabled={busy} onClick={approveSend}>
                Approve &amp; send
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setEdit(body)
                  setPhase('editing')
                }}
              >
                Edit
              </button>
              <button type="button" className="quiet" disabled={busy} onClick={() => dismiss()}>
                Cancel
              </button>
            </>
          ) : null}
          {phase === 'editing' ? (
            <>
              <button type="button" className="ellis" disabled={busy} onClick={saveEdit}>
                Save changes
              </button>
              <button type="button" disabled={busy} onClick={approveSend}>
                Approve &amp; send
              </button>
              <button
                type="button"
                className="quiet"
                disabled={busy}
                onClick={() => {
                  setEdit(body)
                  setPhase('review')
                }}
              >
                Back
              </button>
            </>
          ) : null}
          {phase === 'generating' ? (
            <button type="button" className="quiet" onClick={() => dismiss()}>
              Cancel
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  )
}
